import { EngineError } from "../types";

export interface ProgramOptions {
  vert: string;
  frag: string;
  /** Shared GLSL chunks prepended to the vertex source (after defines). */
  vertChunks?: string[];
  /** Shared GLSL chunks prepended to the fragment source. */
  fragChunks?: string[];
  defines?: Record<string, number | string>;
  /** Captured interleaved when set. */
  tfVaryings?: string[];
  /** Bound before linking so VAOs work across programs. */
  attribLocations?: Record<string, number>;
}

export interface LinkedProgram {
  program: WebGLProgram;
  /** Cached uniform lookup; unknown/optimized-out names return null. */
  uniform(name: string): WebGLUniformLocation | null;
}

function assemble(
  body: string,
  defines: Record<string, number | string>,
  chunks: string[],
): string {
  const parts = ["#version 300 es"];
  for (const [key, value] of Object.entries(defines)) {
    parts.push(`#define ${key} ${value}`);
  }
  parts.push("precision highp float;", "precision highp int;");
  return parts.join("\n") + "\n" + chunks.join("\n") + "\n" + body;
}

function compileStage(
  gl: WebGL2RenderingContext,
  type: number,
  source: string,
): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new EngineError("shader-compile-failed", "createShader returned null");
  }
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? "(no info log)";
    gl.deleteShader(shader);
    const stage = type === gl.VERTEX_SHADER ? "vertex" : "fragment";
    throw new EngineError(
      "shader-compile-failed",
      `${stage} shader failed to compile:\n${log}`,
    );
  }
  return shader;
}

export function compileProgram(
  gl: WebGL2RenderingContext,
  opts: ProgramOptions,
): LinkedProgram {
  const defines = opts.defines ?? {};
  const vertSrc = assemble(opts.vert, defines, opts.vertChunks ?? []);
  const fragSrc = assemble(opts.frag, defines, opts.fragChunks ?? []);
  const vert = compileStage(gl, gl.VERTEX_SHADER, vertSrc);
  const frag = compileStage(gl, gl.FRAGMENT_SHADER, fragSrc);

  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vert);
    gl.deleteShader(frag);
    throw new EngineError("shader-compile-failed", "createProgram returned null");
  }
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  for (const [name, loc] of Object.entries(opts.attribLocations ?? {})) {
    gl.bindAttribLocation(program, loc, name);
  }
  if (opts.tfVaryings && opts.tfVaryings.length > 0) {
    gl.transformFeedbackVaryings(
      program,
      opts.tfVaryings,
      gl.INTERLEAVED_ATTRIBS,
    );
  }
  gl.linkProgram(program);
  gl.deleteShader(vert);
  gl.deleteShader(frag);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? "(no info log)";
    gl.deleteProgram(program);
    throw new EngineError(
      "shader-compile-failed",
      `program failed to link:\n${log}`,
    );
  }

  const locations = new Map<string, WebGLUniformLocation>();
  const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS) as number;
  for (let i = 0; i < count; i++) {
    const info = gl.getActiveUniform(program, i);
    if (!info) continue;
    const loc = gl.getUniformLocation(program, info.name);
    if (!loc) continue;
    // Arrays report as "name[0]"; expose them under the bare name.
    locations.set(info.name.replace(/\[0\]$/, ""), loc);
  }

  return {
    program,
    uniform: (name: string) => locations.get(name) ?? null,
  };
}
